import { DownloadOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Drawer, Input, Select, Table, Tabs, Tag, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivationRecordRow,
  ActivationSummaryData,
  CommercialDimensionRow,
  CommercialRecordsData,
} from "../../shared/protocols/editor/PtlCommercialAnalyticsQuery";
import { DimensionTable, KpiStrip, TrendPanel } from "./CommercialAnalyticsWidgets";
import { ContentGrid, FilterBar, JsonBlock, Section, SectionHeader } from "./commercialAnalyticsStyles";
import {
  buildCommercialQuery,
  displayUser,
  downloadCsv,
  formatDateTime,
  formatDecimal,
  formatInteger,
  formatPercent,
  optionRows,
  queryCommercialAnalytics,
} from "./commercialAnalyticsUtils";

interface ActivationFilters {
  rangeDays: number;
  platform?: string;
  userId?: string;
  provider?: "ocean_engine" | "tencent_ads";
  channel?: "clickid" | "clue_token" | "gdt_vid" | "tencent_click_id" | "wechat_identity" | "none";
  status?: "pending" | "reporting" | "succeeded" | "failed" | "skipped";
  providerCode?: string;
}

const DEFAULT_FILTERS: ActivationFilters = { rangeDays: 7 };
const PROVIDER_LABELS: Record<string, string> = {
  ocean_engine: "巨量引擎",
  tencent_ads: "腾讯广告",
};
const CHANNEL_LABELS: Record<string, string> = {
  clickid: "Click ID",
  clue_token: "Clue Token",
  gdt_vid: "GDT VID",
  tencent_click_id: "腾讯 Click ID",
  wechat_identity: "微信身份匹配",
  none: "无归因参数",
};
const STATUS_LABELS: Record<string, string> = {
  pending: "等待上报",
  reporting: "正在上报",
  succeeded: "上报成功",
  failed: "上报失败",
  skipped: "已跳过",
};

export default function ActivationAnalyticsView() {
  const [draft, setDraft] = useState<ActivationFilters>(DEFAULT_FILTERS);
  const [filters, setFilters] = useState<ActivationFilters>(DEFAULT_FILTERS);
  const [summary, setSummary] = useState<ActivationSummaryData>();
  const [records, setRecords] = useState<CommercialRecordsData>();
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [detail, setDetail] = useState<ActivationRecordRow>();

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await queryCommercialAnalytics(buildCommercialQuery("activation", "summary", filters, {
        provider: filters.provider,
        channel: filters.channel,
        status: filters.status,
        providerCode: filters.providerCode,
      }));
      if (data.scope === "activation" && "kpis" in data) setSummary(data as ActivationSummaryData);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters]);

  const loadRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const data = await queryCommercialAnalytics(buildCommercialQuery("activation", "records", filters, {
        provider: filters.provider,
        channel: filters.channel,
        status: filters.status,
        providerCode: filters.providerCode,
        page,
        pageSize,
      }));
      if ("rows" in data) setRecords(data as CommercialRecordsData);
    } finally {
      setRecordsLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);
  useEffect(() => { void loadRecords(); }, [loadRecords]);

  const applyFilters = () => {
    setPage(1);
    setFilters({ ...draft });
  };

  const resetFilters = () => {
    setDraft(DEFAULT_FILTERS);
    setPage(1);
    setFilters(DEFAULT_FILTERS);
  };

  const dimensionTabs = useMemo(() => [
    { key: "provider", label: "上报平台", children: <DimensionTable rows={summary?.providers || []} loading={summaryLoading} /> },
    { key: "channel", label: "归因渠道", children: <DimensionTable rows={summary?.channels || []} loading={summaryLoading} /> },
    { key: "status", label: "任务状态", children: <DimensionTable rows={summary?.statuses || []} loading={summaryLoading} /> },
    { key: "platform", label: "平台", children: <DimensionTable rows={summary?.platforms || []} loading={summaryLoading} /> },
    { key: "providerCode", label: "平台状态码", children: <DimensionTable rows={summary?.providerCodes || []} loading={summaryLoading} /> },
  ], [summary, summaryLoading]);

  const exportCurrentData = () => {
    const rows: Array<Record<string, unknown>> = [];
    if (summary) {
      Object.entries(summary.kpis).forEach(([key, value]) => rows.push({ 数据区: "概览", 指标: key, 数值: value }));
      appendDimensions(rows, "上报平台", summary.providers);
      appendDimensions(rows, "归因渠道", summary.channels);
      appendDimensions(rows, "任务状态", summary.statuses);
      appendDimensions(rows, "平台", summary.platforms);
      appendDimensions(rows, "平台状态码", summary.providerCodes);
      summary.failureReasons.forEach((row) => rows.push({ 数据区: "失败原因", 分类: row.key, 上报数: row.count, 玩家数: row.userCount }));
    }
    (records?.rows as ActivationRecordRow[] | undefined)?.forEach((row) => rows.push({
      数据区: "当前页明细",
      时间: formatDateTime(row.createdAt),
      玩家: displayUser(row),
      用户ID: row.userId,
      平台: row.platform,
      上报平台: PROVIDER_LABELS[row.provider] || row.provider,
      行为: row.actionType,
      渠道: row.channel,
      状态: row.status,
      尝试次数: row.attempts,
      平台状态码: row.providerCode,
      失败信息: row.lastError || row.skipReason,
    }));
    downloadCsv(`activation-analytics-${Date.now()}.csv`, rows);
  };

  const kpis = summary?.kpis;
  return (
    <>
      <FilterBar>
        <Select
          value={draft.rangeDays}
          style={{ width: 118 }}
          options={[{ label: "最近 1 天", value: 1 }, { label: "最近 7 天", value: 7 }, { label: "最近 30 天", value: 30 }, { label: "最近 90 天", value: 90 }]}
          onChange={(rangeDays) => setDraft((prev) => ({ ...prev, rangeDays }))}
        />
        <Input allowClear placeholder="平台" value={draft.platform} style={{ width: 125 }} onChange={(event) => setDraft((prev) => ({ ...prev, platform: event.target.value }))} />
        <Input allowClear placeholder="用户 ID" value={draft.userId} style={{ width: 210 }} onChange={(event) => setDraft((prev) => ({ ...prev, userId: event.target.value }))} />
        <Select
          allowClear
          placeholder="上报平台"
          value={draft.provider}
          style={{ width: 125 }}
          options={Object.entries(PROVIDER_LABELS).map(([value, label]) => ({ value, label }))}
          onChange={(provider) => setDraft((prev) => ({ ...prev, provider }))}
        />
        <Select
          allowClear
          placeholder="归因渠道"
          value={draft.channel}
          style={{ width: 140 }}
          options={Object.entries(CHANNEL_LABELS).map(([value, label]) => ({ value, label }))}
          onChange={(channel) => setDraft((prev) => ({ ...prev, channel }))}
        />
        <Select
          allowClear
          placeholder="上报状态"
          value={draft.status}
          style={{ width: 130 }}
          options={Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          onChange={(status) => setDraft((prev) => ({ ...prev, status }))}
        />
        <Select
          allowClear
          showSearch
          placeholder="平台状态码"
          value={draft.providerCode}
          style={{ width: 140 }}
          options={optionRows(summary?.options.providerCodes)}
          onChange={(providerCode) => setDraft((prev) => ({ ...prev, providerCode }))}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>查询</Button>
        <Button onClick={resetFilters}>重置</Button>
        <Button
          icon={<ReloadOutlined />}
          loading={summaryLoading || recordsLoading}
          onClick={() => { void loadSummary(); void loadRecords(); }}
        >
          刷新
        </Button>
        <Button icon={<DownloadOutlined />} onClick={exportCurrentData}>导出 CSV</Button>
      </FilterBar>

      <KpiStrip items={[
        { label: "上报玩家", value: formatInteger(kpis?.userCount), hint: `${formatInteger(kpis?.recordCount)} 条激活任务` },
        { label: "归因玩家", value: formatInteger(kpis?.attributedUserCount), hint: `归因率 ${formatPercent(kpis?.attributionRate)}` },
        { label: "成功玩家", value: formatInteger(kpis?.succeededUserCount), hint: "至少成功回传一次" },
        { label: "上报成功", value: formatInteger(kpis?.succeededCount), hint: `任务成功率 ${formatPercent(kpis?.successRate)}` },
        { label: "上报失败", value: formatInteger(kpis?.failedCount), hint: `跳过 ${formatInteger(kpis?.skippedCount)} 次` },
        { label: "平均尝试", value: formatDecimal(kpis?.averageAttempts), hint: `处理中 ${formatInteger(kpis?.reportingCount)} 次` },
      ]} />

      <ContentGrid>
        <TrendPanel
          title="激活上报趋势"
          rows={summary?.trends || []}
          loading={summaryLoading}
          secondaryLabel="归因"
          secondaryValue={(row) => row.attributedUserCount || 0}
        />
        <Section>
          <SectionHeader>维度分布</SectionHeader>
          <Tabs size="small" items={dimensionTabs} />
        </Section>
      </ContentGrid>

      <Section>
        <SectionHeader>
          <span>失败与跳过原因</span>
          <Typography.Text type="secondary">仅展示前 20 项</Typography.Text>
        </SectionHeader>
        <Table
          rowKey="key"
          size="small"
          loading={summaryLoading}
          dataSource={summary?.failureReasons || []}
          pagination={false}
          scroll={{ x: 360 }}
          columns={[
            { title: "原因", dataIndex: "key", ellipsis: true },
            { title: "次数", dataIndex: "count", width: 100, render: formatInteger },
            { title: "玩家", dataIndex: "userCount", width: 100, render: formatInteger },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader>
          <span>激活明细</span>
          <Typography.Text type="secondary">共 {formatInteger(records?.total)} 条</Typography.Text>
        </SectionHeader>
        <Table<ActivationRecordRow>
          rowKey="_id"
          size="small"
          loading={recordsLoading}
          dataSource={(records?.rows || []) as ActivationRecordRow[]}
          scroll={{ x: 1280 }}
          pagination={{
            current: page,
            pageSize,
            total: records?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPageSize === pageSize ? nextPage : 1);
              setPageSize(nextPageSize);
            },
          }}
          columns={[
            { title: "上报时间", dataIndex: "createdAt", width: 170, render: formatDateTime },
            { title: "玩家", width: 150, ellipsis: true, render: (_, row) => displayUser(row) },
            { title: "平台", dataIndex: "platform", width: 90, render: emptyText },
            { title: "上报平台", dataIndex: "provider", width: 105, render: (value) => PROVIDER_LABELS[value] || value },
            { title: "行为", dataIndex: "actionType", width: 90, render: emptyText },
            { title: "渠道", dataIndex: "channel", width: 115, render: (value) => CHANNEL_LABELS[value] || value },
            { title: "状态", dataIndex: "status", width: 100, render: renderActivationStatus },
            { title: "尝试", dataIndex: "attempts", width: 72, render: formatInteger },
            { title: "状态码", dataIndex: "providerCode", width: 90, render: emptyText },
            { title: "结果信息", width: 260, ellipsis: true, render: (_, row) => row.providerMessage || row.lastError || row.skipReason || "-" },
            { title: "操作", fixed: "right", width: 76, render: (_, row) => <Button type="link" size="small" onClick={() => setDetail(row)}>详情</Button> },
          ]}
        />
      </Section>

      <Drawer title="激活上报详情" width="min(620px, 94vw)" open={!!detail} destroyOnHidden onClose={() => setDetail(undefined)}>
        <JsonBlock>{JSON.stringify(detail, null, 2)}</JsonBlock>
      </Drawer>
    </>
  );
}

function appendDimensions(rows: Array<Record<string, unknown>>, section: string, dimensions: CommercialDimensionRow[]) {
  dimensions.forEach((row) => rows.push({
    数据区: section,
    分类: row.key,
    上报数: row.count,
    玩家数: row.userCount,
    成功数: row.successCount,
    失败数: row.failedCount,
    成功率: formatPercent(row.successRate),
  }));
}

function renderActivationStatus(value: string) {
  const color = value === "succeeded" ? "success" : value === "failed" ? "error" : value === "skipped" ? "default" : "processing";
  return <Tag color={color}>{STATUS_LABELS[value] || value}</Tag>;
}

function emptyText(value: unknown) {
  return value == null || value === "" ? "-" : String(value);
}
